<template>
  <div ref="element" :class="{ fade: active }" style="position: fixed; top: 0; left: 0; right: 0; z-index: 100;">
    <slot />
  </div>
</template>

<script setup>
import { useScroll } from '@vueuse/core'

const props = defineProps({
  active: {
    type: Boolean,
    default: true,
  },
  fadeDistance: {
    type: Number,
    default: 200,
    help: 'Distance in pixels to scroll before fully fading out'
  },
  showDistance: {
    type: Number,
    default: 100,
    help: 'Distance in pixels to scroll up before showing again'
  },
});

const element = ref();
const { y, directions } = useScroll(window, { throttle: 100 });

const lastScrollY = ref(0);
const isHidden = ref(false);

// Track scroll behavior
watch(y, (newY) => {
  const delta = newY - lastScrollY.value;

  if (Math.abs(delta) > 3) { // Minimum threshold to prevent jitter
    if (delta < 0) {
      // Scrolling up - show immediately
      isHidden.value = false;
    } else if (delta > 0 && newY > props.fadeDistance) {
      // Scrolling down past threshold - hide
      isHidden.value = true;
    }
  }

  lastScrollY.value = newY;
});

// Calculate opacity based on scroll position and hidden state
const opacity = computed(() => {
  const scrollY = y.value;

  // If at top, always show
  if (scrollY < 50) return 1;

  // If hidden (scrolled down), fade out
  if (isHidden.value) {
    return 0;
  }

  // Otherwise show
  return 1;
});

// Calculate translateY - moves up as it fades out (inverted opacity)
const translateY = computed(() => {
  const fadeAmount = 1 - opacity.value;
  return -100 * fadeAmount; // Moves up to -100px when fully faded
});
</script>

<style scoped>
  div.fade {
    opacity: v-bind(opacity);
    transform: translateY(v-bind(translateY + 'px'));
    transition: opacity 0.2s ease-out, transform 0.2s ease-out;
  }
</style>
